const epcTds = require('epc-tds');
/*eslint no-fallthrough: */
const types = require('../types');
const digitalLink = require('./digitalLinkUri');

const trueView = require('../tyco/trueView');

/** @type {any} */
const epcTdsAny = epcTds;

const TYCO_PREFIX = '3B';

/** @typedef {import('./types').EPCTag} EPCTag */

/**
 * GS1 `EPC Global` tag standard parsing
 * @see https://www.gs1.org/standards/tds
 * Optionally amended with tyco proprietary trueView  format
 * @param {string} hexOrUrn
 * @param {object} options
 * @param {boolean} [options.tycoTrueView] Whether to include recognition of tyco proprietary TrueView Standard
 * @returns {EPCTag | null}
 */
function parse(hexOrUrn, options = {}) {
    try {
        if (hexOrUrn.length <= 2) {
            return null;
        }

        /* istanbul ignore if */
        if (options.tycoTrueView && hexOrUrn.substring(0, 2) === TYCO_PREFIX) {
            //*** (non-standard) Tyco TrueVUE formats *** //
            const subheader = hexOrUrn.substring(0, 3);
            switch (subheader) {
                //  Vue Serial Authority (vueserauth)
                case types.vueSerAuth.prefix: //hex '3BE':
                    return /** @type {EPCTag} */ (trueView.vueSerAuth.parse(hexOrUrn));
                //Vue License Plate (vuelic)
                //i.e. used for display tags
                case types.vueLic.prefix: //hex '3BF':
                    return /** @type {EPCTag} */ (trueView.vueLic.parse(hexOrUrn));
            }
        }

        // *** GS1 Global Standard *** //
        let epc;
        if (hexOrUrn.startsWith('urn:epc:tag:')) {
            epc = epcTdsAny.fromTagURI(hexOrUrn);
        } else if (hexOrUrn.startsWith('urn:epc:id:')) {
            throw new Error('Unable to extract tag information from EPC Pure Identity URI: ' + hexOrUrn);
        } else {
            epc = epcTdsAny.valueOf(hexOrUrn);
        }
        const tagUri = epc.toTagURI();
        const pureIdUri = epc.toIdURI();
        let barcode = null;
        if (typeof epc.toBarcode === 'function') {
            try {
                barcode = epc.toBarcode();
            } catch {
                barcode = null;
            }
        }

        switch (epc.getType().toLowerCase()) {
            // Serialized Global Trade Item Number              (INVENTORY ITEMS)
            case types.sgtin96.id: // hex 30/decimal 48/binary 00110000: SGTIN-96
            case types.sgtin198.id: //hex 36/decimal 54/binary 00110110: SGTIN-198
                return {
                    hex: epc.toHexString().toUpperCase(),
                    type: epc.getType().toLowerCase(),
                    company: epc.getCompanyPrefix(),
                    productNr: epc.getItemReference(),
                    serial: epc.getSerial(),
                    filter: epc.getFilter(),
                    partition: epc.getPartition(),
                    gtin: barcode,
                    // the UPC code is the last 12 digits of the GTIN
                    upc: barcode ? barcode.slice(-12) : null,
                    toString: () => tagUri,
                    toIdUri: () => pureIdUri,
                    toTitle: () => {
                        const safeBarcode = requireBarcodeForTitle(barcode, 'sgtin');
                        return `(01)${safeBarcode.padStart(14, '0')} (21)${epc.getSerial()}`;
                    }
                };
            // Global Location Number With or Without Extension (LOCATIONS)
            case types.sgln96.id: //hex 32/decimal 50/binary 00110010: SGLN-96
            case types.sgln195.id: //hex 39/decimal 57/binary 00111001: SGLN-195
                return {
                    hex: epc.toHexString().toUpperCase(),
                    type: epc.getType().toLowerCase(),
                    company: epc.getCompany(),
                    location: epc.getLocation(),
                    extension: epc.getExtension(),
                    toString: () => tagUri,
                    toIdUri: () => pureIdUri,
                    toTitle: () => {
                        const safeBarcode = requireBarcodeForTitle(barcode, 'sgln');
                        const extension = epc.getExtension();
                        return extension != null && extension !== ''
                            ? `(414)${safeBarcode} (254)${extension}`
                            : `(414)${safeBarcode}`;
                    }
                };
            // Serial Shipping Container Code
            case types.sscc96.id: //hex 31/decimal 49/binary 00110110: SSCC-96
                return {
                    hex: epc.toHexString().toUpperCase(),
                    type: epc.getType().toLowerCase(),
                    company: epc.getCompanyPrefix(),
                    serial: epc.getSerialReference(),
                    toString: () => tagUri,
                    toIdUri: () => pureIdUri,
                    toTitle: () => {
                        const safeBarcode = requireBarcodeForTitle(barcode, 'sscc');
                        return `(00)${safeBarcode.padStart(18, '0')}`;
                    }
                };
            // Global Individual Asset Identifier               (ASSETS)
            case types.giai96.id: //hex 34/decimal 52/binary 00110110: GIAI-96
            case types.giai202.id: //hex 38/decimal 56/binary 00111000: GIAI-202
                return {
                    hex: epc.toHexString().toUpperCase(),
                    type: epc.getType().toLowerCase(),
                    company: epc.getCompanyPrefix(),
                    assetReference: epc.getAssetReference(),
                    toString: () => tagUri,
                    toIdUri: () => pureIdUri,
                    toTitle: () => `(8004)${barcode || `${epc.getCompanyPrefix()}${epc.getAssetReference()}`}`
                };
            // Global Returnable Asset Identifier                (RTIs/Pallets)
            case types.grai96.id: //hex 33/decimal 51/binary 00110011: GRAI-96
            case types.grai170.id: //hex 37/decimal 55/binary 00110111: GRAI-170
                return {
                    hex: epc.toHexString().toUpperCase(),
                    type: epc.getType().toLowerCase(),
                    company: epc.getCompanyPrefix(),
                    assetType: epc.getAssetType(),
                    serial: epc.getSerial(),
                    toString: () => tagUri,
                    toIdUri: () => pureIdUri,
                    toTitle: () => {
                        const safeBarcode = requireBarcodeForTitle(barcode, 'grai');
                        return `(8003)${safeBarcode}`;
                    }
                };
            // General Identifier
            case types.gid96.id: //hex 35/decimal 53/binary 00110110: GID-96
                return {
                    hex: epc.toHexString().toUpperCase(),
                    type: epc.getType().toLowerCase(),
                    /** General Manager Number */
                    company: epc.getManager(),
                    /** Object Class */
                    productNr: epc.getClass(),
                    serial: epc.getSerial(),
                    toString: () => tagUri,
                    toIdUri: () => pureIdUri,
                    // Internal display format only: GID has no GS1 element-string barcode representation.
                    toTitle: () => `(91)${epc.getClass()} (21)${epc.getSerial()}`
                };
            // Global Service Relation Number – Recipient
            case types.gsrn96.id: //hex 2D/decimal 45/binary 00101101: GSRN-96
                return {
                    hex: epc.toHexString().toUpperCase(),
                    type: epc.getType().toLowerCase(),
                    company: epc.getCompanyPrefix(),
                    serviceReference: epc.getServiceReference(),
                    toString: () => tagUri,
                    toIdUri: () => pureIdUri,
                    toTitle: () => `(8018)${requireBarcodeForTitle(barcode, 'gsrn')}`
                };
            // Global Document Type Identifier                  (DOCUMENTS)
            case types.gdti96.id: //hex 2C/decimal 44/binary 00101100: GDTI-96
            case types.gdti174.id: //hex 3E/decimal 62/binary 00111110: GDTI-174
                return {
                    hex: epc.toHexString().toUpperCase(),
                    type: epc.getType().toLowerCase(),
                    company: epc.getCompanyPrefix(),
                    documentType: epc.getDocumentReference(),
                    serial: epc.getSerial(),
                    toString: () => tagUri,
                    toIdUri: () => pureIdUri,
                    toTitle: () => `(253)${requireBarcodeForTitle(barcode, 'gdti')}`
                };
            // Component / Part Identifier
            case types.cpi96.id: //hex 3C/decimal 60/binary 00111100: CPI-96
                return {
                    hex: epc.toHexString().toUpperCase(),
                    type: epc.getType().toLowerCase(),
                    company: epc.getCompanyPrefix(),
                    componentPartReference: epc.getPartReference(),
                    serial: epc.getSerial(),
                    toString: () => tagUri,
                    toIdUri: () => pureIdUri,
                    // (8010) CPID has no check digit; fallback concatenation is spec-correct.
                    toTitle: () => `(8010)${barcode || `${epc.getCompanyPrefix()}${epc.getPartReference()}`} (8011)${epc.getSerial()}`
                };
            // Serialized Global Coupon Number
            case types.sgcn96.id: //hex 3F/decimal 63/binary 00111111: SGCN-96
                return {
                    hex: epc.toHexString().toUpperCase(),
                    type: epc.getType().toLowerCase(),
                    company: epc.getCompanyPrefix(),
                    couponReference: epc.getCouponReference(),
                    serial: epc.getSerial(),
                    toString: () => tagUri,
                    toIdUri: () => pureIdUri,
                    toTitle: () => `(255)${requireBarcodeForTitle(barcode, 'sgcn')}`
                };
            default:
                break;
        }

        //not supported in epc-tds lib yet as of 7/19/2024. Leaving in for documentation
        let header = hexOrUrn.substring(0, 2);
        let result = null;
        /*istanbul ignore next */
        switch (header.toUpperCase()) {
            // Global Service Relation Number – Provider
            case '2E': //hex 2E/decimal 46/binary 00101110: GSRNP-96
            // Global Document Type Identifier                  (DOCUMENTS)
            case '3A': //hex 3A/decimal 58/binary 00111010: GDTI-113 //DEPRECATED
            // Component / Part Identifier
            case '3D': //hex 3D/decimal 49/binary 00111101: CPI-var
            // US Department of Defense Identifier
            case '2F': //hex 2F/decimal 47/binary 00101111: USDoD-96
            // Aerospace and Defense Identifier
            case '3B': //hex 3B/decimal 59/binary 00110110: ADI-var , if not (non-standard) Tyco TrueVUE
            default:
                result = null;
                break;
        }
        return result;
        /*istanbul ignore next */
    } catch (ex) {
        /** @type {Error} */
        const originalError = ex instanceof Error ? ex : new Error(String(ex));
        const newError = new Error('Unable to Parse ' + hexOrUrn + ': ' + originalError.message);
        newError.stack = originalError.stack;

        throw newError;
    }
}

/**
 * @param {string | null} barcode
 * @param {string} type
 */
function requireBarcodeForTitle(barcode, type) {
    if (!barcode) {
        throw new Error(`Unable to generate ${type} title: barcode value unavailable`);
    }
    return barcode;
}

/* istanbul ignore next */
/**
 * https://ref.gs1.org/test-suites/2d-barcodes-in-retail
 * Parse GS1 (sunrise 2027) barcodes
 * @param {string} barcode
 * @returns {{title:string, serialNo?:string, lotNo?:string, expirationDate?:string, 'item:Type'?:{title:string}}|null}
 */
function parseBarcode(barcode) {
    if (!barcode) {
        return null;
    }
    if (barcode.startsWith('https://')) {
        return digitalLink.parse(barcode);
    }
    return { title: barcode };
}

module.exports = {
    parse,
    parseBarcode,
    mapToItem: require('./mapToItem'),
    encodeSgtin: require('./encodeSgtin')
};
